import * as RadixSwitch from '@radix-ui/react-switch';
import React from 'react';
import { FieldCompatibleProps } from '../Field';
import { useFullTheme } from '~/design';
import { useId } from '~/utils';
import { LabelText } from '~/internal';

export interface SwitchProps extends FieldCompatibleProps<boolean> {
  /** The aria-label of the switch. */
  'aria-label'?: string;

  /** The aria-labelledby of the switch. */
  'aria-labelledby'?: string;

  /** If true, the switch will be disabled. */
  disabled?: boolean;

  /** The label for the switch. */
  label?: string;

  /** The placement of the label, relative to the switch. (default: right) */
  labelPlacement?: 'left' | 'right';

  /** The width of the switch container. 'hug' results in inline behaviour, 'fill' results in
   * block behaviour. (default: hug) */
  width?: 'hug' | 'fill';
}

export function Switch({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  disabled = false,
  label,
  labelPlacement = 'right',
  onChange,
  value,
  width = 'hug',
}: SwitchProps) {
  const id = useId();
  const theme = useFullTheme();
  if (!label && !ariaLabel && !ariaLabelledby) {
    throw new Error(
      'One of `label`, `aria-label`, or `aria-labelledby` is required for accessibility reasons.',
    );
  }
  if ([label, ariaLabel, ariaLabelledby].filter(Boolean).length > 1) {
    throw new Error('Only one of `label`, `aria-label`, `aria-labelledby` can be used.');
  }

  const labelComponent = label ? (
    <label
      // legacy.min.css has several CSS rules that target the label tag
      css={{ margin: 0 }}
      htmlFor={id}
      id={`label-${id}`}
    >
      <LabelText disabled={disabled} variant="bodyM">
        {label}
      </LabelText>
    </label>
  ) : null;

  return (
    <div
      css={{
        display: width === 'hug' ? 'inline-flex' : 'flex',
        gap: theme.spacing[2],
        justifyContent: width === 'fill' ? 'space-between' : undefined,
      }}
    >
      {labelPlacement === 'left' && labelComponent}
      <RadixSwitch.Root
        aria-labelledby={ariaLabelledby ? ariaLabelledby : ariaLabel ? undefined : `label-${id}`}
        aria-label={ariaLabel}
        css={[
          {
            backgroundColor: theme.colors.bgRegular,
            borderStyle: 'solid',
            borderColor: disabled ? theme.colors.borderSecondary : theme.colors.textSecondary,
            borderWidth: theme.spacing['2px'],
            borderRadius: theme.spacing[3],
            cursor: 'pointer',
            height: theme.spacing[6],
            padding: theme.spacing['2px'],
            width: theme.spacing[12],
            '&:focus-visible': {
              outline: `3px solid ${theme.colors.actionPrimary}`,
              outlineOffset: '1px',
            },
            '&:hover': {
              boxShadow: `0px 0px 0px ${theme.spacing[2]} ${theme.colors.bgSecondaryHighlight}`, // TODO: use token?
            },
          },
          value && {
            backgroundColor: disabled ? theme.colors.borderSecondary : theme.colors.actionPrimary,
            borderColor: disabled ? theme.colors.borderSecondary : theme.colors.actionPrimary,
          },
          disabled && {
            cursor: 'not-allowed',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        ]}
        checked={value}
        disabled={disabled}
        id={id}
        onCheckedChange={onChange}
      >
        <RadixSwitch.Thumb
          css={[
            {
              display: 'block',
              backgroundColor: disabled
                ? theme.colors.borderSecondary
                : theme.colors.actionSecondary,
              height: theme.spacing[4],
              borderRadius: theme.borderRadiuses.circle,
              width: theme.spacing[4],
            },
            value && {
              backgroundColor: theme.colors.bgRegular,
              marginLeft: 'auto',
            },
          ]}
          data-testid="crystal-ui-switch-circle"
        />
      </RadixSwitch.Root>
      {labelPlacement === 'right' && labelComponent}
    </div>
  );
}
